import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { User, Mail, Lock, MapPin, AlertCircle, Eye, EyeOff, ArrowLeft, Building, Map, Hash, Globe } from 'lucide-react';
import {
  PHONE_LENGTH,
  sanitizeText,
  sanitizePhone,
  validatePhoneNumber,
  validateFullName,
  validateEmail,
  validatePassword,
  validateAddress,
  validateZipCode,
  getPasswordStrength
} from '../utils/validation';
import './Register.css';

const INITIAL_FORM_DATA = {
  fullName: '',
  email: '',
  phoneNumber: '',
  password: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: ''
};


const Register = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleBackToLogin = () => {
    resetRegisterForm();
    navigate('/login');
  };

  const [formData, setFormData] = useState({
    phoneNumber: location.state?.phoneNumber || '',
    fullName: '',
    email: '',
    password: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: ''
  });

  const resetRegisterForm = () => {
    setFormData({
      ...INITIAL_FORM_DATA,
      phoneNumber: location.state?.phoneNumber || ''
    });
    setShowPassword(false);
    setGeneralError('');
  };

  const [showPassword, setShowPassword] = useState(false);
  const [generalError, setGeneralError] = useState('');

  // Ensure form is fresh on every mount and clear on unmount
  useEffect(() => {
    resetRegisterForm();
    return () => {
      resetRegisterForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Consolidate all validation into a single memoized object to avoid redundant calls in render
  const {
    validationErrors,
    hasBlockingErrors,
    passwordStrength,
    isSubmitDisabled
  } = useMemo(() => {
    // Single calls for each field validation
    const pError = validatePhoneNumber(formData.phoneNumber);
    const nError = validateFullName(formData.fullName.trim());
    const eError = validateEmail(formData.email.trim());
    const passError = validatePassword(formData.password);
    const aError = validateAddress(formData.address);
    const zError = validateZipCode(formData.zip);

    // Address error is stored for UI but NOT included in blocking check
    const blockingErrorsExist = !!(pError || nError || eError || passError || zError);
    const requiredFieldsEmpty = !formData.fullName.trim() || !formData.email.trim() || !formData.password;

    return {
      validationErrors: {
        phoneNumber: pError,
        fullName: nError,
        email: eError,
        password: passError,
        address: aError,
        zip: zError
      },
      hasBlockingErrors: blockingErrorsExist,
      passwordStrength: getPasswordStrength(formData.password),
      isSubmitDisabled: blockingErrorsExist || requiredFieldsEmpty
    };
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    // Real-time sanitization and input sanity
    if (name === 'phoneNumber') {
      finalValue = sanitizePhone(value).slice(0, PHONE_LENGTH);
    } else if (name === 'fullName') {
      // Prevent < > and script-like chars from entering state
      finalValue = value.replace(/[<>]/g, '').slice(0, 50);
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
    setGeneralError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    // Blocking errors (excluding address) prevent submission
    if (hasBlockingErrors) {
      return;
    }

    // Single calls for final data preparation
    const finalData = {
      ...formData,
      fullName: sanitizeText(formData.fullName),
      email: formData.email.trim(),
      phoneNumber: sanitizePhone(formData.phoneNumber),
      address: sanitizeText(formData.address),
      city: sanitizeText(formData.city),
      state: sanitizeText(formData.state),
      zip: sanitizeText(formData.zip),
      country: sanitizeText(formData.country)
    };

    const result = await register(finalData);
    if (result.success) {
      resetRegisterForm();
      navigate('/home');
    } else {
      setGeneralError(result.error);
    }
  };

  return (
    <div className="register-container">
      <header className="register-header">
        <button onClick={handleBackToLogin} className="back-nav">
          <ArrowLeft size={18} />
          Back
        </button>
      </header>
      <div className="register-card fade-in">
        <div className="register-icon">
          <img src="/logo.png" alt="Ithal by MR Logo" className="register-logo" />
        </div>

        <h1 className="register-title">Create Account</h1>
        <p className="register-subtitle">Join our designer community</p>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="phone-display">
            <span>{formData.phoneNumber}</span>
          </div>
          {formData.phoneNumber && validationErrors.phoneNumber && (
            <p className="error-text">{validationErrors.phoneNumber}</p>
          )}

          <div className="form-group">
            <label>Full Name *</label>
            <div className="input-wrapper">
              <User size={20} className="input-icon" />
              <input
                type="text"
                name="fullName"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleChange}
                required
                maxLength={50}
                autoComplete="name"
              />
            </div>
            {formData.fullName && validationErrors.fullName && (
              <p className="error-text">{validationErrors.fullName}</p>
            )}
          </div>

          <div className="form-group">
            <label>Email *</label>
            <div className="input-wrapper">
              <Mail size={20} className="input-icon" />
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>
            {formData.email && validationErrors.email && (
              <p className="error-text">{validationErrors.email}</p>
            )}
          </div>

          <div className="form-group">
            <label>Password *</label>
            <div className="input-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Create a password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formData.password && (
              <div className="password-review">
                <p className="review-title">Password Requirements:</p>
                <div className="review-items">
                  <div className={`review-item ${passwordStrength.hasMinLength ? 'valid' : 'invalid'}`}>
                    <span className="review-icon">{passwordStrength.hasMinLength ? '✅' : '❌'}</span>
                    <span>8+ characters</span>
                  </div>
                  <div className={`review-item ${passwordStrength.hasUppercase ? 'valid' : 'invalid'}`}>
                    <span className="review-icon">{passwordStrength.hasUppercase ? '✅' : '❌'}</span>
                    <span>1 uppercase letter</span>
                  </div>
                  <div className={`review-item ${passwordStrength.hasLowercase ? 'valid' : 'invalid'}`}>
                    <span className="review-icon">{passwordStrength.hasLowercase ? '✅' : '❌'}</span>
                    <span>1 lowercase letter</span>
                  </div>
                  <div className={`review-item ${passwordStrength.hasNumber ? 'valid' : 'invalid'}`}>
                    <span className="review-icon">{passwordStrength.hasNumber ? '✅' : '❌'}</span>
                    <span>1 number</span>
                  </div>
                  <div className={`review-item ${passwordStrength.hasSpecialChar ? 'valid' : 'invalid'}`}>
                    <span className="review-icon">{passwordStrength.hasSpecialChar ? '✅' : '❌'}</span>
                    <span>1 special character (@#$!%*?&)</span>
                  </div>
                </div>
              </div>
            )}
            {formData.password && validationErrors.password && (
              <p className="error-text">{validationErrors.password}</p>
            )}
          </div>

          <div className="form-group">
            <label>Address (Optional)</label>
            <div className="input-wrapper">
              <MapPin size={20} className="input-icon" />
              <input
                type="text"
                name="address"
                placeholder="Enter your address"
                value={formData.address}
                onChange={handleChange}
                autoComplete="street-address"
              />
            </div>
            {formData.address && validationErrors.address && (
              <p className="error-text">{validationErrors.address}</p>
            )}
          </div>

          <div className="form-row-group">
            <div className="form-group">
              <label>City</label>
              <div className="input-wrapper">
                <Building size={20} className="input-icon" />
                <input
                  type="text"
                  name="city"
                  placeholder="Enter City"
                  value={formData.city}
                  onChange={handleChange}
                  autoComplete="address-level2"
                />
              </div>
            </div>

            <div className="form-group">
              <label>State</label>
              <div className="input-wrapper">
                <Map size={20} className="input-icon" />
                <input
                  type="text"
                  name="state"
                  placeholder="Enter State"
                  value={formData.state}
                  onChange={handleChange}
                  autoComplete="address-level1"
                />
              </div>
            </div>
          </div>

          <div className="form-row-group">
            <div className="form-group">
              <label>Zip Code</label>
              <div className="input-wrapper">
                <Hash size={20} className="input-icon" />
                <input
                  type="text"
                  name="zip"
                  placeholder="Enter Zip Code"
                  value={formData.zip}
                  onChange={handleChange}
                  autoComplete="postal-code"
                />
              </div>
              {formData.zip && validationErrors.zip && (
                <p className="error-text">{validationErrors.zip}</p>
              )}
            </div>

            <div className="form-group">
              <label>Country</label>
              <div className="input-wrapper">
                <Globe size={20} className="input-icon" />
                <input
                  type="text"
                  name="country"
                  placeholder="Enter Country"
                  value={formData.country}
                  onChange={handleChange}
                  autoComplete="country-name"
                />
              </div>
            </div>
          </div>

          {generalError && (
            <div className="error-message fade-in">
              <AlertCircle size={16} />
              <span>{generalError}</span>
            </div>
          )}

          <button
            type="submit"
            className={`btn-primary ${isSubmitDisabled ? 'disabled' : ''}`}
            disabled={isSubmitDisabled}
          >
            Create Account →
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;